<template>
  <div>
    <div class="p-4 bg-secondary text-white">
      <h4 class="uppercase mb-0">{{ $t('titles.ammunition') }}</h4>
    </div>
    <div class="p-4 bg-white border-b">
      <div class="form-group">
        <label class="form-label">
          {{ $t("ammunition.manufacturer") }}
        </label>
        <select
          name="ammunitionBrand"
          class="form-control with-caret"
          :class="{error: errors.has('ammunitionBrand')}"
          :placeholder="$t('ammunition.manufacturerPlaceholder')"
          v-model="currentAmmunitionBrand">
          <option :value="null" disabled>{{ $t("ammunition.manufacturerPlaceholder") }}</option>
          <option value="other">{{ $t("ammunition.customAmmunition") }}</option>
          <option :value="index" v-for="(brand, index) in ammuntionBrands">{{brand}}</option>
        </select>

      </div>

      <div class="form-group" :class="{'hidden': !isExistingAmmunitionItem(currentAmmunitionBrand)}">
        <label class="form-label">
          {{ $t("ammunition.caliber") }}
        </label>
        <select
          name="ammunitionCaliber"
          class="with-caret form-control"
          :class="{'is-disabled': !isExistingAmmunitionItem(currentAmmunitionBrand), error: errors.has('ammunitionCaliber')}"
          :placeholder="$t('ammunition.caliberPlaceholder')"
          v-model="currentAmmunitionCaliber">
          <option :value="null" disabled>{{ $t("ammunition.caliberPlaceholder") }}</option>
          <option :value="index" v-for="(caliber, index) in ammunitionCalibers">{{caliber}}</option>
          <option :value="null" disabled>{{ $t("ammunition.customAmmunitionPrompt") }}</option>
          <option value="other">{{ $t("ammunition.customAmmunition") }}</option>
        </select>
      </div>

      <div class="form-group" :class="{'hidden': !isExistingAmmunitionItem(currentAmmunitionCaliber)}">
        <label class="form-label">
          {{ $t("ammunition.nameAndWeight") }}
        </label>
        <select
          class="with-caret form-control"
          name="currentAmmunitionSlug"
          :class="{'is-disabled': !isExistingAmmunitionItem(currentAmmunitionCaliber), error: errors.has('currentAmmunitionSlug')}"
          :placeholder="$t('ammunition.nameAndWeightPlaceholder')"
          v-model="currentAmmunitionSlug">
          <option :value="null" disabled>{{ $t("ammunition.nameAndWeightPlaceholder") }}</option>
          <option :value="ammunition.slug" v-for="ammunition in ammunitions">{{ammunitionLabel(ammunition)}}</option>
          <option :value="null" disabled>{{ $t("ammunition.customAmmunitionPrompt") }}</option>
          <option value="other">{{ $t("ammunition.customAmmunition") }}</option>
        </select>

        <input type="hidden" name="turret_customization[ammunition_slug]" :value="currentAmmunitionSlugFormValue"></input>
      </div>

      <div class="p-4 text-sm text-gray-700 flex items-center justify-between text-center">
        <i class="fas fa-arrow-down animate-bounce text-primary"></i>
        <div class="px-4" v-if="isExistingAmmunitionItem(currentAmmunitionSlug)">{{ $t("ammunition.verifyAmmunitionDetails") }}</div>
        <div class="px-4" v-else>{{ $t("ammunition.provideAmmunitionDetails") }}</div>
        <i class="fas fa-arrow-down animate-bounce text-primary"></i>
      </div>
    </div>

    <div class="p-4">
      <div class="flex flex-col lg:flex-row lg:flex-wrap">
        <!-- Drag Coefficient -->
        <div class="form-group w-full lg:w-1/3 lg:pr-3">
          <label class="form-label" for="drag_coefficient">
            <span>{{ $t("dragCoefficient.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('dragCoefficient.helpText')">
            </i>
          </label>

          <input
            name="turret_customization[drag_coefficient]"
            type="number"
            step="any"
            :placeholder="$t('dragCoefficient.placeholder')"
            class="form-control"
            :class="{'is-disabled': shouldDisableAmmunitionSpecificFields, 'error': errors.has('turret_customization[drag_coefficient]')}"
            :readonly="shouldDisableAmmunitionSpecificFields"
            :data-vv-as="$t('dragCoefficient.label')"
            v-validate="'required|between:0.1,1.2'"
            v-model="dragCoefficient">
          </input>
          <span class="form-hint error">{{ errors.first("turret_customization[drag_coefficient]") }}</span>
        </div>

        <!-- Drag Model -->
        <div class="form-group w-full lg:w-1/3 lg:pr-3">
          <label class="form-label" for="drag_function">
            <span>{{ $t("dragModel.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('dragModel.helpText')">
            </i>
          </label>

          <select
            name="turret_customization[drag_function]"
            class="form-control select with-caret"
            :class="{'is-disabled': shouldDisableAmmunitionSpecificFields}"
            :readonly="shouldDisableAmmunitionSpecificFields"
            v-model="dragFunction">
            <option value="1">G1</option>
            <option value="7">G7</option>
          </select>
        </div>

        <!-- Ammunition Weight -->
        <div class="form-group w-full lg:w-1/3">
          <label class="form-label" for="ammunition_weight">
            <span>{{ $t("ammunitionWeight.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('ammunitionWeight.helpText')">
            </i>
          </label>

          <div class="input-group">
            <input
              name="turret_customization[ammunition_weight]"
              type="number"
              step="any"
              placeholder="Weight (e.g. 200)"
              class="form-control"
              :class="{'is-disabled': shouldDisableAmmunitionSpecificFields, 'error': errors.has('turret_customization[ammunition_weight]')}"
              :readonly="shouldDisableAmmunitionSpecificFields"
              :data-vv-as="$t('ammunitionWeight.label')"
              v-validate="'required|min_value:0'"
              v-model="ammunitionWeight">
            </input>
            <div class="input-group-append">
              <div class="input-group-text text-xs uppercase">{{ $t("generic.grains") }}</div>
            </div>
          </div>
          <span class="form-hint error">{{ errors.first("turret_customization[ammunition_weight]") }}</span>
        </div>
      </div>
      <div class="p-4 bg-gray-100 text-center border rounded text-sm text-gray-700">
        <span v-if="isExistingAmmunitionItem(currentAmmunitionSlug)">{{$t("ammunition.formHint")}}</span>
        <span>{{ $t("ammunition.formHelper") }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    currentAmmunition() { return this.$store.getters["turretCustomizer/currentAmmunition"] },
    ammunitions() { return this.$store.getters["turretCustomizer/ammunitions"] },
    ammuntionBrands() { return this.$store.getters["turretCustomizer/ammuntionBrands"] },
    ammunitionCalibers() { return this.$store.getters["turretCustomizer/ammunitionCalibers"] },
    currentAmmunitionBrand: {
      get() { return this.getSelectValueFrom("turretCustomizer/currentAmmunitionBrand", this.ammuntionBrands) },
      set(brand) { this.setSelectValue("turretCustomizer/updateAmmunitionBrand", this.ammuntionBrands, brand) },
    },
    currentAmmunitionCaliber: {
      get() { return this.getSelectValueFrom("turretCustomizer/currentAmmunitionCaliber", this.ammunitionCalibers) },
      set(caliber) { this.setSelectValue("turretCustomizer/updateAmmunitionCaliber", this.ammunitionCalibers, caliber) },
    },
    currentAmmunitionSlug: {
      get() {
        if (!this.isExistingAmmunitionItem(this.currentAmmunition)) {
          return this.currentAmmunition
        }

        return this.currentAmmunition.slug
      },
      set(newCurrentAmmunitionSlug) { this.$store.dispatch("turretCustomizer/updateCurrentAmmunition", newCurrentAmmunitionSlug) }
    },
    dragCoefficient: {
      get() { return this.$store.getters["turretCustomizer/dragCoefficient"] },
      set(newDragCoefficient) { this.$store.dispatch("turretCustomizer/updateDragCoefficient", newDragCoefficient) }
    },
    dragFunction: {
      get() { return this.$store.getters["turretCustomizer/dragFunction"] },
      set(newDragFunction) { this.$store.dispatch("turretCustomizer/updateDragFunction", newDragFunction) }
    },
    ammunitionWeight: {
      get() { return this.$store.getters["turretCustomizer/ammunitionWeight"] },
      set(newAmmunitionWeight) { this.$store.dispatch("turretCustomizer/updateAmmunitionWeight", newAmmunitionWeight) }
    },
    shouldDisableAmmunitionSpecificFields() {
      return this.isExistingAmmunitionItem(this.currentAmmunitionSlug)
    },
    currentAmmunitionSlugFormValue() {
      return this.isExistingAmmunitionItem(this.currentAmmunition) ? this.currentAmmunition.slug : null
    }
  },
  methods: {
    getSelectValueFrom(getterName, items) {
      let value = this.$store.getters[getterName]

      if (this.isExistingAmmunitionItem(value)) {
        value = items.indexOf(value)
      }

      return value
    },
    setSelectValue(actionName, items, value) {
      const newValue = value !== "other" ? items[value] : value
      this.$store.dispatch(actionName, newValue)
    },
    isExistingAmmunitionItem(ammunitionItem) {
      return ammunitionItem != null && ammunitionItem != "other"
    },
    ammunitionLabel(ammunition) {
      return `${ammunition.name} -- ${ammunition.weight}GR`
    }
  }
}
</script>
